import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger.js';
import '../css/main.css';

gsap.registerPlugin(ScrollTrigger);

/* ============================================================
   0. 常量：所有"魔法数字"集中在这里，方便调参
   ============================================================ */
const MODEL_MAX_SIZE = 2.45;   // 归一化后模型最大边的长度
const BASE_SCALE = 6;          // 飞机模型根节点缩放
const BASE_POSITION = new THREE.Vector3(0.28, 0.10, 0.60);  // 飞机基准位置

// 滚动动画的目标位置。第一版里 modelGroup 本身带 scale，数值即世界坐标；
// 现在 scrollGroup 嵌在 scale=BASE_SCALE 的 modelRoot 内部，必须换算成局部坐标。
const SCROLL_TARGET_WORLD = new THREE.Vector3(-1.1, 0.2, 0.2);

const GLB_URL = './assets/aircraft/star_sparrow_modular_spaceship.glb';
const MECHA_FLOAT_BASE = -1.12;  // 机甲上下漂浮的基准 y（第一版为负值）
const MECHA_FLOAT_AMP = 0.018;   // 漂浮振幅

const prefersReducedMotion =
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ============================================================
   1. DOM 查询
   ============================================================ */
const canvas = document.querySelector('#scene-canvas');
const status = document.querySelector('.model-status');
const statusText = document.querySelector('.status-text');
const fallback = document.querySelector('.fallback-message');

/* ============================================================
   2. 渲染器 / 相机 / 场景
   ============================================================ */
const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 100);
camera.position.set(0, 0.1, 7.4);

let renderer;
try {
  renderer = new THREE.WebGLRenderer({
    canvas,
    alpha: true,
    antialias: true,
    powerPreference: 'high-performance',
  });
} catch (error) {
  // WebGL 不可用 → 显示 DOM 兜底提示
  if (fallback) fallback.hidden = false;
  if (status) status.hidden = true;
  throw error;
}

renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.setClearColor(0x08090a, 0);

/* ============================================================
   3. 环境贴图（按第一版移除）
   ------------------------------------------------------------
   第一版没有 scene.environment，金属件完全靠下面三盏灯打亮。
   挂上 RoomEnvironment 会让模型整体提亮、观感和第一版不一致，
   因此这里不启用。若以后想要更亮的金属反射，再把它加回来。
   ============================================================ */

/* ============================================================
   4. 场景层级 —— 这是本次修复的核心
   ------------------------------------------------------------
   scene
     ├── stage           粒子（缓慢自转）
     └── modelRoot       固定基础位置 + 缩放
          └── scrollGroup  ← GSAP 滚动驱动（position + rotation）
               └── tiltGroup ← 鼠标视差驱动（rotation.x / z）
                    └── model  真正的模型对象

   为什么要拆三层？
   因为 position/rotation 是同一个属性通道，两个动画系统同时写
   会互相覆盖。拆到不同节点上，各自只动自己那一份。
   ============================================================ */
const stage = new THREE.Group();
const modelRoot = new THREE.Group();
const scrollGroup = new THREE.Group();
const tiltGroup = new THREE.Group();

modelRoot.position.copy(BASE_POSITION);
modelRoot.scale.setScalar(BASE_SCALE);
modelRoot.add(scrollGroup);
scrollGroup.add(tiltGroup);
scene.add(stage, modelRoot);

/* ============================================================
   5. 灯光
   ============================================================ */
scene.add(new THREE.HemisphereLight(0xdffcff, 0x071018, 2.2));

const keyLight = new THREE.DirectionalLight(0xbceeff, 4.2);
keyLight.position.set(-4, 5, 6);
scene.add(keyLight);

const rimLight = new THREE.PointLight(0x1bc9ff, 22, 9, 2);
rimLight.position.set(3, 2, -1);
scene.add(rimLight);

/* ============================================================
   6. 状态变量
   ============================================================ */
const pointer = new THREE.Vector2();
const pointerTarget = new THREE.Vector2();
const clock = new THREE.Clock();
const gltfLoader = new GLTFLoader();

let model = null;
let mixer = null;
let isProceduralModel = false;

/* ============================================================
   7. 工具函数
   ============================================================ */

/** 视口自适应 */
function resize() {
  const width = window.innerWidth;
  const height = window.innerHeight;
  camera.aspect = width / height;
  camera.updateProjectionMatrix();
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(width, height, false);
}

/** 更新加载状态文案（防 NaN） */
function setStatus(message, progress = null) {
  if (!statusText) return;
  statusText.textContent =
    progress === null || !Number.isFinite(progress)
      ? message
      : `${message} ${Math.round(progress)}%`;
}

/** 延迟淡出状态提示 */
function hideStatus() {
  if (!status) return;
  window.setTimeout(() => { status.style.opacity = '0'; }, 1200);
}

/** 释放 GPU 资源（切模型 / 卸载页面时用） */
function disposeObject(object) {
  object.traverse((child) => {
    if (child.geometry) child.geometry.dispose();
    const materials = Array.isArray(child.material)
      ? child.material
      : [child.material];
    materials.forEach((m) => m && m.dispose());
  });
}

/** 把任意尺寸的模型归一化到 MODEL_MAX_SIZE 并居中 */
function normalizeModel(object) {
  const bounds = new THREE.Box3().setFromObject(object);
  const size = bounds.getSize(new THREE.Vector3());
  const center = bounds.getCenter(new THREE.Vector3());
  const maxSide = Math.max(size.x, size.y, size.z) || 1;
  const scale = MODEL_MAX_SIZE / maxSide;

  object.scale.setScalar(scale);
  object.position.set(-center.x * scale, -center.y * scale, -center.z * scale);
}

/** 统一调整材质，让 GLB 和程序化模型观感一致 */
function tuneMaterials(object, { metalness, roughness } = {}) {
  object.traverse((child) => {
    if (!child.isMesh || !child.material) return;
    const materials = Array.isArray(child.material)
      ? child.material
      : [child.material];

    materials.forEach((material) => {
      if ('metalness' in material && metalness !== undefined) {
        material.metalness = Math.max(material.metalness, metalness);
      }
      if ('roughness' in material && roughness !== undefined) {
        material.roughness = Math.min(material.roughness, roughness);
      }
      material.needsUpdate = true;
    });
  });
}

/* ============================================================
   8. 粒子背景
   ============================================================ */
function createParticles() {
  const count = window.innerWidth < 700 ? 420 : 900;
  const positions = new Float32Array(count * 3);

  for (let i = 0; i < count; i += 1) {
    const radius = 3.5 + Math.random() * 5.5;
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1); // 球面均匀采样
    positions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
    positions[i * 3 + 1] = radius * Math.cos(phi) * 0.74;
    positions[i * 3 + 2] = radius * Math.sin(phi) * Math.sin(theta) - 1.5;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

  const material = new THREE.PointsMaterial({
    color: 0xa8b9bd,
    size: 0.018,
    transparent: true,
    opacity: 0.46,
    depthWrite: false,
  });

  const particles = new THREE.Points(geometry, material);
  stage.add(particles);
  return particles;
}

/* ============================================================
   9. 程序化机甲（GLB 加载失败时的兜底）
   ============================================================ */
function makePart(geometry, material, position, rotation = [0, 0, 0]) {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.set(...position);
  mesh.rotation.set(...rotation);
  return mesh;
}

function createMecha() {
  const mecha = new THREE.Group();

  const armor = (color, emissive = color) =>
    new THREE.MeshStandardMaterial({
      color,
      metalness: 0.72,
      roughness: 0.25,
      emissive,
      emissiveIntensity: 0.09,
    });

  const blue = armor('#2f9cb2', '#063744');
  const white = armor('#dbe8e9', '#426b75');
  const dark = armor('#172934', '#06131a');
  const red = armor('#ff5b3d', '#60170e');
  const gold = armor('#f5bd59', '#6b3b06');

  const edge = new THREE.LineBasicMaterial({
    color: '#78f4ff', transparent: true, opacity: 0.18,
  });
  const glow = new THREE.MeshBasicMaterial({ color: '#78f4ff' });

  // 带描边的面板
  function panel(geometry, material, position, rotation = [0, 0, 0]) {
    const mesh = makePart(geometry, material, position, rotation);
    mesh.add(new THREE.LineSegments(new THREE.EdgesGeometry(geometry, 25), edge));
    return mesh;
  }

  /* ---- 躯干 ---- */
  mecha.add(panel(new RoundedBoxGeometry(0.9, 1.05, 0.58, 4, 0.08), blue, [0, 1.55, 0]));
  mecha.add(panel(new RoundedBoxGeometry(0.62, 0.3, 0.48, 4, 0.06), white, [0, 0.98, 0.02]));
  mecha.add(panel(new RoundedBoxGeometry(0.78, 0.38, 0.48, 4, 0.06), white, [0, 0.78, 0.01]));
  mecha.add(panel(new RoundedBoxGeometry(0.45, 0.38, 0.4, 4, 0.07), white, [0, 2.25, 0.01]));
  mecha.add(panel(new RoundedBoxGeometry(0.29, 0.13, 0.06, 4, 0.025), dark, [0, 2.25, 0.235]));
  mecha.add(makePart(new RoundedBoxGeometry(0.16, 0.045, 0.025, 4, 0.01), glow, [0, 2.27, 0.27]));

  /* ---- 头饰 ---- */
  mecha.add(panel(new THREE.ConeGeometry(0.045, 0.42, 4), gold, [-0.13, 2.63, 0.02], [0, 0, -0.28]));
  mecha.add(panel(new THREE.ConeGeometry(0.045, 0.42, 4), gold, [0.13, 2.63, 0.02], [0, 0, 0.28]));
  mecha.add(panel(new THREE.ConeGeometry(0.06, 0.25, 4), red, [0, 2.45, 0.02], [Math.PI, 0, 0]));

  /* ---- 左右对称部件：用循环避免重复代码 ---- */
  for (const side of [-1, 1]) {
    mecha.add(panel(new RoundedBoxGeometry(0.42, 0.34, 0.62, 4, 0.07), blue, [side * 0.68, 1.82, 0]));
    mecha.add(panel(new RoundedBoxGeometry(0.28, 0.66, 0.32, 4, 0.06), white, [side * 0.82, 1.27, 0]));
    mecha.add(panel(new THREE.CylinderGeometry(0.13, 0.13, 0.18, 12), dark, [side * 0.82, 1.63, 0], [0, 0, Math.PI / 2]));
    mecha.add(panel(new RoundedBoxGeometry(0.32, 0.62, 0.34, 4, 0.06), blue, [side * 0.82, 0.72, 0]));
    mecha.add(panel(new RoundedBoxGeometry(0.4, 0.2, 0.64, 4, 0.05), white, [side * 0.82, 0.27, 0.12]));
    mecha.add(panel(new THREE.CylinderGeometry(0.1, 0.1, 0.5, 8), dark, [side * 0.82, 1.03, 0], [0, 0, Math.PI / 2]));
  }

  /* ---- 胸口 / 背部 ---- */
  mecha.add(panel(new RoundedBoxGeometry(0.8, 0.16, 0.2, 4, 0.04), red, [0, 1.68, 0.32]));
  for (const x of [-0.2, 0, 0.2]) {
    mecha.add(makePart(new RoundedBoxGeometry(0.1, 0.06, 0.035, 3, 0.01), glow, [x, 1.5, 0.3]));
  }
  mecha.add(panel(new RoundedBoxGeometry(0.18, 1.0, 0.1, 4, 0.02), dark, [0, 1.48, -0.37]));
  mecha.add(panel(new RoundedBoxGeometry(0.74, 1.0, 0.1, 4, 0.02), blue, [0, 1.55, -0.45]));

  const backpack = new THREE.Group();
  backpack.add(panel(new RoundedBoxGeometry(0.62, 0.7, 0.24, 4, 0.06), dark, [0, 1.52, -0.58]));
  backpack.add(panel(new RoundedBoxGeometry(0.14, 0.72, 0.12, 4, 0.025), blue, [-0.42, 1.62, -0.56], [0, 0, -0.16]));
  backpack.add(panel(new RoundedBoxGeometry(0.14, 0.72, 0.12, 4, 0.025), blue, [0.42, 1.62, -0.56], [0, 0, 0.16]));
  mecha.add(backpack);

  /* ---- 盾牌 ---- */
  const shield = new THREE.Group();
  shield.add(panel(new RoundedBoxGeometry(0.3, 1.06, 0.12, 4, 0.04), blue, [0, 0, 0]));
  shield.add(panel(new RoundedBoxGeometry(0.18, 0.64, 0.04, 4, 0.015), white, [0, 0.03, 0.075]));
  shield.add(makePart(new RoundedBoxGeometry(0.12, 0.08, 0.025, 3, 0.01), red, [0, 0.3, 0.1]));
  shield.position.set(-1.16, 1.02, 0.12);
  shield.rotation.z = -0.1;
  mecha.add(shield);

  /* ---- 步枪 ---- */
  const rifle = new THREE.Group();
  rifle.add(panel(new RoundedBoxGeometry(0.16, 0.8, 0.16, 4, 0.03), dark, [0, 0, 0]));
  rifle.add(panel(new RoundedBoxGeometry(0.1, 0.3, 0.12, 4, 0.02), blue, [0, 0.42, 0]));
  rifle.add(makePart(new THREE.CylinderGeometry(0.045, 0.045, 0.5, 8), glow, [0, 0.86, 0]));
  rifle.position.set(1.13, 0.98, 0.18);
  rifle.rotation.z = -0.17;
  mecha.add(rifle);

  /* ---- 光环 ---- */
  const ring = new THREE.Mesh(
    new THREE.TorusGeometry(1.18, 0.012, 8, 96),
    new THREE.MeshBasicMaterial({ color: '#78f4ff', transparent: true, opacity: 0.68 }),
  );
  ring.rotation.x = Math.PI / 2.3;
  ring.position.y = 1.35;
  mecha.add(ring);

  mecha.scale.setScalar(0.88);
  mecha.position.y = MECHA_FLOAT_BASE;
  mecha.userData.ring = ring;
  return mecha;
}

/* ============================================================
   10. 加载 GLB（失败则退回程序化机甲）
   ============================================================ */
function loadScene() {
  gltfLoader.load(
    // 相对路径以【文档地址】为基准解析，而非 JS 文件位置
    GLB_URL,

    /* ---- 成功 ---- */
    (gltf) => {
      disposeObject(tiltGroup);
      tiltGroup.clear();

      model = gltf.scene;
      normalizeModel(model);
      tuneMaterials(model, { metalness: 0.35, roughness: 0.62 });
      tiltGroup.add(model);

      // 初始朝向写在 scrollGroup 上（与第一版一致）：
      // 第一版是在同一个 modelGroup 上从 -0.35 补间到 π*1.2；
      // 若把 -0.35 放在 tiltGroup，滚动终点会比第一版少转 0.35 弧度。
      scrollGroup.rotation.y = -0.35;

      mixer = gltf.animations.length ? new THREE.AnimationMixer(model) : null;
      if (mixer) mixer.clipAction(gltf.animations[0]).play();

      setStatus('Star Sparrow ready', 100);
      hideStatus();
      setupScrollMotion();
    },

    /* ---- 进度 ---- */
    (event) => {
      if (event.total > 0) {
        setStatus('Loading aircraft', (event.loaded / event.total) * 100);
      } else {
        setStatus('Loading aircraft');
      }
    },

    /* ---- 失败 ---- */
    (error) => {
      console.warn('[scene] GLB 加载失败，切换到程序化模型', error);
      if (fallback) fallback.hidden = false;

      disposeObject(tiltGroup);
      tiltGroup.clear();

      model = createMecha();
      tiltGroup.add(model);
      scrollGroup.rotation.y = -0.35;
      isProceduralModel = true;

      setStatus('Mini mecha fallback', 100);
      hideStatus();
      setupScrollMotion();
    },
  );
}

/* ============================================================
   11. GSAP 滚动动画（只操作 scrollGroup，不和鼠标抢属性）
   ============================================================ */
function setupScrollMotion() {
  if (prefersReducedMotion) return;

  const trigger = document.querySelector('.statement');
  if (!trigger) return;

  // 世界坐标 → 局部坐标：先减去基准位置，再除以 BASE_SCALE
  // （不做这一步，位移会被放大 6 倍，模型直接被甩出画面）
  const target = SCROLL_TARGET_WORLD.clone()
    .sub(BASE_POSITION)
    .divideScalar(BASE_SCALE);

  gsap.timeline({
    scrollTrigger: {
      trigger,
      start: 'top bottom',
      end: 'bottom top',
      scrub: 1.2,   // 滚动跟随的"重量感"
    },
  })
    .to(scrollGroup.position, { x: target.x, y: target.y, z: target.z, ease: 'none' }, 0)
    .to(scrollGroup.rotation, { x: 0.2, y: Math.PI * 1.2, z: -0.1, ease: 'none' }, 0)
    .to(camera.position, { z: 6.15, ease: 'none' }, 0);
}

/* ============================================================
   12. 渲染循环
   ============================================================ */
function animate() {
  const delta = clock.getDelta();
  const elapsed = clock.elapsedTime;

  if (mixer) mixer.update(delta);

  if (!prefersReducedMotion) {
    // 指针平滑（指数插值）
    pointer.lerp(pointerTarget, 0.045);

    if (model) {
      // 视差倾斜 → 写 tiltGroup，不会和 GSAP 冲突
      tiltGroup.rotation.x += (pointer.y * 0.12 - tiltGroup.rotation.x) * 0.025;
      tiltGroup.rotation.z += (-pointer.x * 0.1 - tiltGroup.rotation.z) * 0.025;

      // 机甲上下漂浮
      if (isProceduralModel) {
        model.position.y =
          MECHA_FLOAT_BASE + Math.sin(elapsed * 0.75) * MECHA_FLOAT_AMP;
      }
      // 光环自转
      if (model.userData.ring) {
        model.userData.ring.rotation.z = elapsed * 0.32;
      }
    }

    // 粒子群缓慢自转
    stage.rotation.y = elapsed * 0.015;
  }

  renderer.render(scene, camera);
  requestAnimationFrame(animate);
}

/* ============================================================
   13. 事件绑定 & 启动
   ============================================================ */
window.addEventListener('resize', resize);

window.addEventListener(
  'pointermove',
  (event) => {
    pointerTarget.x = (event.clientX / window.innerWidth) * 2 - 1;
    pointerTarget.y = (event.clientY / window.innerHeight) * 2 - 1;
  },
  { passive: true },
);

resize();
createParticles();
loadScene();
animate();
